import React from 'react';
import { NotificationStatus } from '@cp-shared-19/frontend-ui';
import { Notification } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';
import { EditStatus } from '../enums';

type AddressChangeNotificationProps = {
    lastEditStatus: EditStatus;
    effectDate?: string;
};

type NotificationProps = {
    testId: string;
    status: NotificationStatus;
    headline?: string;
    text: string | React.ReactNode;
};

export const AddressChangeNotification: React.FC<AddressChangeNotificationProps> = ({ lastEditStatus }) => {
    const { t } = useTranslation('my-profile');

    if (lastEditStatus === EditStatus.NOT_PERFORMED || lastEditStatus === EditStatus.NO_EMAIL_ERROR) {
        return null;
    }

    const getNotificationProps = (): NotificationProps => {
        switch (lastEditStatus) {
            case EditStatus.CHECK_FAILURE:
                return {
                    testId: 'check-failure',
                    status: NotificationStatus.error,
                    headline: t('address.notification.check-failure.text'),
                    text: (
                        <>
                            {t('address.notification.check-failure.option1')}
                            <br aria-hidden="true" />
                            {t('address.notification.check-failure.option2')}
                        </>
                    ),
                };
            case EditStatus.ERROR:
                return {
                    testId: 'no-connection-error',
                    status: NotificationStatus.error,
                    text: t('address.notification.error'),
                };
            case EditStatus.ERROR_DOCUMENTS:
                return {
                    testId: 'documents-error',
                    status: NotificationStatus.error,
                    text: t('address.notification.error-documents'),
                };
            case EditStatus.PROOF_OF_ADDRESS_MANDATORY:
                return {
                    testId: 'no-proof_of_address',
                    status: NotificationStatus.error,
                    headline: t('address.notification.address-failure.text'),
                    text: (
                        <ul>
                            <li>{t('address.notification.address-failure.option1')}</li>
                            <li>{t('address.notification.address-failure.option2')}</li>
                            <li>{t('address.notification.address-failure.option3')}</li>
                            <li>{t('address.notification.address-failure.option4')}</li>
                        </ul>
                    ),
                };
            case EditStatus.ALREADY_IN_PROGRESS:
                return {
                    testId: 'already-in-progress',
                    status: NotificationStatus.warning,
                    text: t('address.notification.already-in-progress'),
                };
            case EditStatus.SUCCESS:
                return {
                    testId: 'success',
                    status: NotificationStatus.success,
                    headline: t('address.notification.success.title'),
                    text: t('address.notification.success.text'),
                };
        }
    };

    const { text, headline, ...notificationProps } = getNotificationProps();

    return (
        <Notification
            {...notificationProps}
            className={'u-mb'}
            title={headline ?? ''}
            hideTitle={!headline}
            visible
            showCloseButton={false}
        >
            {text}
        </Notification>
    );
};
